import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ParkingSquare, MapPin, LogOut, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import { useAuth } from "@/context/AuthContext";
import { setActiveTimeZone } from "@/lib/datetime";

export default function SeleccionSucursal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listSucursales()
      .then(({ data }) => {
        // Sprint 32.0 — filtrar SOLO las sucursales asignadas al perfil del colaborador (UI, sin tocar el servidor)
        const allowedIds = new Set([...(user?.sucursales_ids || []), ...(user?.id_sucursal ? [user.id_sucursal] : [])]);
        const propias = user?.todas_las_sucursales ? data : data.filter((s) => allowedIds.has(s.id_sucursal));
        setSucursales(propias);
        // Si solo tiene UNA sucursal, saltar este paso y entrar directo
        if (propias.length === 1) { pick(propias[0]); return; }
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (s) => {
    localStorage.setItem("pap_sucursal", JSON.stringify({ id: s.id_sucursal, nombre: s.nombre, modelo_cobro: s.modelo_cobro || "COMBINADO", zona_horaria: s.zona_horaria || "America/Mexico_City" }));
    setActiveTimeZone(s.zona_horaria || "America/Mexico_City");
    // Sprint 29.0 — en sucursales SOLO_VALET la Terminal se oculta: todos operan desde el tablero Valet
    if (s.modelo_cobro === "SOLO_VALET") { navigate("/operativo/valet"); return; }
    navigate(user?.rol === "Valet" ? "/operativo/valet" : "/operativo");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <header className="h-20 border-b border-white/10 flex items-center justify-between px-6 lg:px-10">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-[#FFCC00] flex items-center justify-center">
            <ParkingSquare className="h-5 w-5 text-[#0A0A0A]" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-display font-black text-sm tracking-tight leading-none">PARKING ADMIN PRO</p>
            <p className="text-[10px] tracking-[0.2em] text-[#FFCC00] font-bold mt-1 uppercase">{user?.usuario_corto} · {user?.rol}</p>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="seleccion-logout-btn"
          className="btn-hard h-10 px-4 bg-white/5 border border-white/20 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-white/10">
          <LogOut className="h-4 w-4" /> Salir
        </button>
      </header>

      <div className="flex-1 px-6 lg:px-10 py-12 max-w-4xl w-full mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFCC00] mb-2">Contexto operativo</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-10">Selecciona tu sucursal</h1>

          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#FFCC00]" /></div>
          ) : sucursales.length === 0 ? (
            <div className="py-20 text-center text-white/50" data-testid="seleccion-empty">
              <MapPin className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p>No hay sucursales disponibles para tu empresa.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="seleccion-grid">
              {sucursales.map((s) => (
                <button
                  key={s.id_sucursal}
                  onClick={() => pick(s)}
                  data-testid={`seleccion-sucursal-${s.id_sucursal}`}
                  className="btn-hard group text-left bg-white/5 border border-white/20 hover:border-[#FFCC00] hover:bg-white/10 p-6 min-h-[120px] flex flex-col justify-between transition-colors duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 bg-[#FFCC00]/10 border border-[#FFCC00]/40 flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-[#FFCC00]" />
                    </div>
                    <ChevronRight className="h-5 w-5 text-white/40 group-hover:text-[#FFCC00] transition-colors duration-200" />
                  </div>
                  <div className="mt-4">
                    <p className="font-display text-xl font-bold tracking-tight">{s.nombre}</p>
                    <p className="text-xs text-white/50 mt-1 uppercase tracking-wider">Saldo: {s.saldo_folios.toLocaleString()} folios</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
